import { Users, Layers, Cloud, Code2, BrainCircuit, ScrollText, Database, Server, LifeBuoy } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import Reveal, { stagger, fadeUp } from "@/components/motion/Reveal";

// 9 services — aligned 3×3 bento (6-col grid, every card spans 2)
const ACCENT = [false, false, false, false, true, false, false, false, false]; // Cloud & Managed Services = dark feature
const ICONS  = [Users, Layers, Database, Server, Cloud, LifeBuoy, Code2, BrainCircuit, ScrollText];

export default function Services() {
  const { t } = useI18n();
  const s = t.services;
  return (
    <section id="services" data-testid="services-section" className="section-pad bg-white relative">
      <div className="absolute inset-0 noise-overlay pointer-events-none" aria-hidden />
      <div className="container-pro relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <Reveal>
            <div className="overline">{s.kicker}</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight">
              {s.title1}
              <br className="hidden md:block" /> {s.title2}
            </h2>
          </Reveal>
          <Reveal variants={fadeUp}>
            <p className="max-w-md text-ink-secondary">{s.subtitle}</p>
          </Reveal>
        </div>

        <Reveal variants={stagger(0.05, 0.07)} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {s.list.map((it, i) => {
            const Icon = ICONS[i];
            return (
              <ServiceCard key={i} icon={<Icon size={20} />} title={it.title} body={it.body} accent={ACCENT[i]} index={i} />
            );
          })}
        </Reveal>
      </div>
    </section>
  );
}

const ServiceCard = ({ icon, title, body, accent = false, index }) => (
  <Reveal variants={fadeUp} className="h-full">
    <article
      data-testid="service-card"
      className={`group relative h-full rounded-xl p-7 md:p-8 border transition-all duration-300 hover:-translate-y-1 ${
        accent
          ? "bg-surface-dark text-white border-line-dark hover:shadow-[0_16px_48px_-12px_rgba(24,119,242,0.35)]"
          : "bg-white border-line hover:border-brand-red/40 hover:shadow-[0_16px_48px_-16px_rgba(24,119,242,0.25)]"
      }`}
    >
      {accent && <div className="absolute inset-0 dot-bg opacity-30 rounded-xl pointer-events-none" aria-hidden />}
      <div className="relative">
        <div className="flex items-center justify-between">
          <span className={`inline-flex h-10 w-10 items-center justify-center rounded-md transition-colors duration-300 ${
            accent ? "bg-white/10 text-white group-hover:bg-brand-red" : "bg-surface-muted text-brand-red group-hover:bg-brand-red group-hover:text-white"
          }`}>
            {icon}
          </span>
          <span className={`font-mono text-[10px] tracking-widest ${accent ? "text-white/40" : "text-ink-muted"}`}>
            / {String(index + 1).padStart(2, "0")}
          </span>
        </div>
        <h3 className={`mt-6 font-heading text-xl md:text-2xl font-medium tracking-tight leading-tight ${accent ? "text-white" : "text-ink-primary"}`}>{title}</h3>
        <p className={`mt-3 text-sm leading-relaxed ${accent ? "text-white/70" : "text-ink-secondary"}`}>{body}</p>
      </div>
    </article>
  </Reveal>
);
